import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  onSnapshot, 
  deleteDoc 
} from 'firebase/firestore';
import { 
  Plus, 
  Trash2, 
  Search,
  FolderOpen,
  LogOut,
  User,
  Briefcase,
  PieChart,
  LayoutGrid,
  List,
  X
} from 'lucide-react';

// --- Shared Components (Can be moved to a shared file later) ---
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-200 ${className}`}>
    {children}
  </div>
);

const Badge = ({ children, type }) => {
  const styles = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pitch: 'bg-blue-100 text-blue-700 border-blue-200',
    complete: 'bg-slate-100 text-slate-700 border-slate-200',
    archived: 'bg-slate-100 text-slate-500 border-slate-200',
    lost: 'bg-rose-100 text-rose-700 border-rose-200'
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[type.toLowerCase()] || styles.active}`}>
      {children}
    </span>
  );
};

const Modal = ({ isOpen, onClose, title, children, maxWidth = "max-w-md" }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`bg-white rounded-xl shadow-xl ${maxWidth} w-full overflow-hidden animate-in fade-in zoom-in duration-200`}>
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export default function ProjectsList({ user, db, appId, onSelectProject, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Form State
  const [newProject, setNewProject] = useState({
    name: '',
    clientId: '',
    budgetFees: '',
    budgetExpenses: '',
    status: 'Active'
  });
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');

  // --- Data Loading ---
  useEffect(() => {
    if (!user) return;

    // Load Projects
    const projectsRef = collection(db, 'artifacts', appId, 'users', user.uid, 'projects');
    const unsubProjects = onSnapshot(projectsRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(loaded);
    }, (error) => console.error("Error loading projects:", error));

    // Load Clients
    const clientsRef = collection(db, 'artifacts', appId, 'users', user.uid, 'clients');
    const unsubClients = onSnapshot(clientsRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(loaded);
    }, (error) => console.error("Error loading clients:", error));

    return () => {
      unsubProjects();
      unsubClients();
    };
  }, [user, db, appId]);

  // --- Actions ---
  const handleCreateProject = async () => {
    if (!user || !newProject.name) return;

    let finalClientId = newProject.clientId;
    let finalClientName = clients.find(c => c.id === newProject.clientId)?.name || '';

    // Create Client if needed
    if (isAddingClient && newClientName) {
      try {
        const clientRef = doc(collection(db, 'artifacts', appId, 'users', user.uid, 'clients'));
        await setDoc(clientRef, { name: newClientName });
        finalClientId = clientRef.id;
        finalClientName = newClientName;
      } catch (e) {
        console.error("Error creating client:", e);
        return;
      }
    }

    // Create Project
    try {
      const projectRef = doc(collection(db, 'artifacts', appId, 'projects'));
      await setDoc(projectRef, {
        name: newProject.name,
        clientId: finalClientId,
        clientName: finalClientName,
        budgetFees: Number(newProject.budgetFees) || 0,
        budgetExpenses: Number(newProject.budgetExpenses) || 0,
        status: newProject.status,
        createdAt: new Date().toISOString(),
        createdBy: user.uid // Track who created it
      });
      closeModal();
    } catch (e) {
      console.error("Error creating project:", e);
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation(); // Prevent navigation
    if (!window.confirm("Are you sure? This will delete the project and all its milestones.")) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'projects', projectId));
    } catch (e) {
      console.error("Error deleting project:", e);
    }
  };

  // --- Filtering ---
  const filteredProjects = useMemo(() => {
    return projects.filter(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.clientName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [projects, searchQuery]);

  // --- Helpers ---
  const openModal = () => {
    setNewProject({ name: '', clientId: '', budgetFees: '', budgetExpenses: '', status: 'Active' });
    setIsAddingClient(false);
    setNewClientName('');
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);
  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        
        <header className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Projects</h1>
            <p className="text-slate-500 mt-1">Overview of all your client projects and budgets.</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-3 items-center">
              <div className="flex items-center gap-3 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm mr-2">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="User" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                    <User size={14} />
                  </div>
                )}
                <span className="text-sm font-medium text-slate-700 hidden sm:block">
                  {user.displayName || 'User'}
                </span>
                <button 
                  onClick={onLogout}
                  className="text-slate-400 hover:text-slate-600 ml-2 transition-colors"
                  title="Sign Out"
                >
                  <LogOut size={16} />
                </button>
              </div>

              <button 
                onClick={openModal}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-all shadow-sm hover:shadow active:scale-95"
              >
                <Plus size={18} />
                New Project
              </button>
            </div>
          </div>
        </header>

        <div className="mb-6 flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search projects or clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm"
            />
          </div>
          <div className="flex bg-white rounded-lg border border-slate-200 p-1 shadow-sm">
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button 
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200 border-dashed">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
              <FolderOpen size={32} />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-1">No projects found</h3>
            <p className="text-slate-500 mb-4">Get started by creating your first client project.</p>
            <button 
              onClick={openModal}
              className="text-blue-600 font-medium hover:underline"
            >
              Create New Project
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((p) => (
              <Card 
                key={p.id} 
                className="hover:shadow-md transition-shadow cursor-pointer group relative"
              >
                <div onClick={() => onSelectProject(p)} className="p-6 h-full flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                      <Briefcase size={24} />
                    </div>
                    <Badge type={p.status}>{p.status}</Badge>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">{p.name}</h3>
                  <p className="text-sm text-slate-500 mb-4">{p.clientName || 'No Client'}</p>
                  
                  <div className="mt-auto pt-4 border-t border-slate-100 flex justify-between items-center text-sm">
                    <span className="text-slate-600 font-medium">{formatCurrency((p.budgetFees || 0) + (p.budgetExpenses || 0) + (p.budget || 0))}</span>
                    <button 
                      onClick={(e) => handleDeleteProject(e, p.id)}
                      className="text-slate-400 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-rose-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 font-medium">Project Name</th>
                  <th className="px-6 py-3 font-medium">Client</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium text-right">Budget</th>
                  <th className="px-6 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProjects.map((p) => (
                  <tr 
                    key={p.id} 
                    onClick={() => onSelectProject(p)}
                    className="hover:bg-slate-50 cursor-pointer group"
                  >
                    <td className="px-6 py-4 font-medium text-slate-900">{p.name}</td>
                    <td className="px-6 py-4 text-slate-600">{p.clientName}</td>
                    <td className="px-6 py-4">
                      <Badge type={p.status}>{p.status}</Badge>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-slate-600">{formatCurrency((p.budgetFees || 0) + (p.budgetExpenses || 0) + (p.budget || 0))}</td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={(e) => handleDeleteProject(e, p.id)}
                        className="text-slate-400 hover:text-rose-500 p-1.5 hover:bg-rose-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title="New Project">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Project Name</label>
            <input 
              type="text" 
              value={newProject.name}
              onChange={(e) => setNewProject({...newProject, name: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              placeholder="e.g. Website Redesign"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
            {isAddingClient ? (
               <div className="flex gap-2">
                 <input 
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="Client Name"
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  autoFocus
                 />
                 <button 
                  onClick={() => setIsAddingClient(false)}
                  className="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg border border-slate-200"
                 >
                   Cancel
                 </button>
               </div>
            ) : (
              <div className="flex gap-2">
                <select 
                  value={newProject.clientId}
                  onChange={(e) => {
                    if (e.target.value === 'new') {
                      setIsAddingClient(true);
                      setNewProject({...newProject, clientId: ''});
                    } else {
                      setNewProject({...newProject, clientId: e.target.value});
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="">Select Client...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                  <option value="new" className="text-blue-600 font-medium">+ Add New Client</option>
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fees Budget ($)</label>
              <input 
                type="number" 
                value={newProject.budgetFees}
                onChange={(e) => setNewProject({...newProject, budgetFees: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Exp. Budget ($)</label>
              <input 
                type="number" 
                value={newProject.budgetExpenses}
                onChange={(e) => setNewProject({...newProject, budgetExpenses: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select 
              value={newProject.status}
              onChange={(e) => setNewProject({...newProject, status: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
            >
              <option>Pitch</option>
              <option>Active</option>
              <option>Complete</option>
              <option>Archived</option>
              <option>Lost</option>
            </select>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <button 
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleCreateProject}
              disabled={!newProject.name || (isAddingClient && !newClientName) || (!isAddingClient && !newProject.clientId)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Project
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
